import {IS_SINGLE_PLAYER_VALUE} from "data/constants";
import {Bindings} from "data/constants/bindings";
import {RoundStatus, RoundType} from "data/enums";
import {IPickResponse} from "data/providers/api/pinpoint.api.provider";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPinpointStore} from "data/stores/pinpoint/pinpoint.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {ViewController} from "data/types/structure";
import {convertYardToFeet, convertYardToFeetAndInches, getPlayerShortName} from "data/utils/helper";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";

export interface IRoundResultsProps {
	roundId: number;
}

export interface IRoundResultsController extends ViewController<IRoundResultsProps> {
	get i18n(): ILocalizationStore;
	get myPick(): IPickResponse | null;
	get hasPick(): boolean;
	get isCompleted(): boolean;
	get stats(): Record<
		RoundType,
		{
			isPlayersCorrect: boolean;
			isAnswerCorrect: boolean;
			bestResult: string;
		}
	>;
	get isSinglePlayer(): boolean;
}

@injectable()
export class RoundResultsController implements IRoundResultsController {
	@observable _roundId?: number;

	constructor(
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PinpointStore) private _pinpointStore: IPinpointStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get myPick() {
		if (!this._roundId) {
			return null;
		}

		return this._pinpointStore.getPickByRoundId(this._roundId);
	}

	get hasPick() {
		return Boolean(this.myPick);
	}

	get round() {
		if (!this._roundId) {
			return null;
		}

		return this._roundStore.getRoundById(this._roundId);
	}

	get golfers() {
		return this.round?.golfers || [];
	}

	get golfersPairs() {
		return this.golfers.reduce((acc, el) => {
			acc.push([...(el.usa ?? [])]);
			acc.push([...(el.europe ?? [])]);
			return acc;
		}, [] as number[][]);
	}

	get isSinglePlayer() {
		return Boolean(this.round?.name.toLowerCase().includes(IS_SINGLE_PLAYER_VALUE));
	}

	private getRoundTypeData = (type: RoundType) => {
		const roundTypeData = {
			[RoundType.LONGESTDRIVE]: {
				playerId: this.myPick?.longestDriveLineup?.golfers[0],
				value: this.myPick?.longestDriveLineup?.value,
				hole: Number(this.round?.longestDriveHole) || 0,
				field: "distance" as const,
			},
			[RoundType.NEARESTPIN]: {
				playerId: this.myPick?.nearestPinLineup?.golfers[0],
				value: this.myPick?.nearestPinLineup?.value,
				hole: Number(this.round?.nearestPinHole) || 0,
				field: "distanceToPin" as const,
			},
		};

		return roundTypeData[type];
	};

	private parseValueArray = (value?: string) => {
		return value
			?.split(",")
			.filter((i) => i !== "")
			.map((i) => Number(i));
	};

	private findPlayerIdsByPlayerId = (playerId?: number) => {
		if (!playerId) {
			return [];
		}

		if (this.isSinglePlayer) {
			return [playerId];
		}

		return this.golfersPairs.find((el) => el.includes(playerId)) || [];
	};

	private getIsAnswerCorrect = ({
		valueArr,
		value,
		type,
	}: {
		valueArr?: number[];
		value?: number;
		type: RoundType;
	}) => {
		const isLongestDrive = type === RoundType.LONGESTDRIVE;

		if (!valueArr || !value) {
			return false;
		}

		if (valueArr?.length === 1) {
			return isLongestDrive ? value > valueArr[0] : value >= valueArr[0];
		}

		if (valueArr?.length === 2) {
			return isLongestDrive
				? [value >= valueArr[0], value <= valueArr[1]].every(Boolean)
				: [value >= valueArr[0], value < valueArr[1]].every(Boolean);
		}

		return false;
	};

	get stats() {
		const result = [RoundType.LONGESTDRIVE, RoundType.NEARESTPIN].map((type) => {
			const {playerId, value, hole, field} = this.getRoundTypeData(type);

			const valueArr = this.parseValueArray(value);
			const playerIds = this.findPlayerIdsByPlayerId(playerId);

			const longestDriveResult = this._playerStore.getLongestDrive(this._roundId!, hole);
			const nearestPinResult = this._playerStore.getNearestPin(this._roundId!, hole);

			const resultByType = {
				[RoundType.LONGESTDRIVE]: longestDriveResult,
				[RoundType.NEARESTPIN]: nearestPinResult,
			}[type];

			const isPlayersCorrect = playerIds.includes(resultByType?.playerId || 0);

			const valueByType = {
				[RoundType.LONGESTDRIVE]: resultByType?.[field],
				[RoundType.NEARESTPIN]: resultByType?.[field]
					? convertYardToFeet(resultByType?.[field])
					: undefined,
			}[type];

			const isAnswerCorrect = this.getIsAnswerCorrect({
				valueArr,
				value: valueByType,
				type,
			});

			const unit = {
				[RoundType.LONGESTDRIVE]: " yards",
				[RoundType.NEARESTPIN]: "'",
			}[type];

			const convertedResult = {
				[RoundType.LONGESTDRIVE]: resultByType?.[field]
					? `${resultByType[field]}${unit}`
					: "",
				[RoundType.NEARESTPIN]: resultByType?.[field]
					? convertYardToFeetAndInches(resultByType?.[field])
					: "",
			}[type];

			const bestResult = resultByType
				? `${getPlayerShortName(
						this._playerStore.getPlayerById(resultByType.playerId)
				  )} - ${convertedResult}`
				: "";

			return {
				isPlayersCorrect,
				isAnswerCorrect: Boolean(isAnswerCorrect),
				bestResult,
			};
		});

		return {
			[RoundType.LONGESTDRIVE]: result[0],
			[RoundType.NEARESTPIN]: result[1],
		};
	}

	get isCompleted() {
		return this.round?.pinpointStatus === RoundStatus.Complete;
	}

	@action init({roundId}: IRoundResultsProps) {
		this._roundId = roundId;
	}
}
